import * as React from "react"
import Mapbox, { MapViewStateChangeEvent } from "../mapbox/Mapbox"
import GeoJSONLayer from "../mapbox/GeoJSONLayer"
import { GeolocateControl, NavigationControl } from "react-map-gl"
import { Center, Spinner, useTheme } from "@chakra-ui/react"
import { ZoneEntity } from "../lib/types/zone"
import { GroupedAlertEntry } from "../lib/types/alert"
import { ALERT_MAP_PADDING, Bbox, getZonesGeojson } from "../lib/helpers/map"
import { ErrorBoundary } from "./ErrorBoundary"
import { useMapBBox } from "./providers/MapBBoxProvider"
import AlertsMapMapboxSupercluster from "./AlertsMapMapboxSupercluster"
import { useFillOpacity } from "../lib/hooks/useDropZoneStyle"

export interface AlertsMapProps {
  bbox: Bbox
  isLoading: boolean
  showServiceZones: boolean
  serviceZones: ZoneEntity[] | undefined
  showDropzones: boolean
  dropzones: ZoneEntity[] | undefined
  showMaintenanceZones: boolean
  maintenanceZones: ZoneEntity[] | undefined
  groupedAlertsEntries: GroupedAlertEntry[]
  refetchAlerts: () => Promise<any>
  selectedBikeUuid?: string
  setSelectedBikeUuid: (uuid: string) => void
  onDeselectBike: () => void
  selectedAlertUuid?: string
  onDeselectAlert: () => void
}

const MAP_ID = "alertsMapbox"

const AlertsMapMapbox: React.FC<AlertsMapProps> = ({
  bbox,
  isLoading,
  showServiceZones,
  serviceZones,
  showDropzones,
  dropzones,
  showMaintenanceZones,
  maintenanceZones,
  groupedAlertsEntries,
  refetchAlerts,
  selectedBikeUuid,
  setSelectedBikeUuid,
  onDeselectBike,
  selectedAlertUuid,
  onDeselectAlert,
}) => {
  const theme = useTheme()
  const { setBBoxFromMapbox } = useMapBBox()

  const handleMapMove = (event: MapViewStateChangeEvent) => {
    if (event.fitboundUpdate) return
    const map = event.target
    const bounds = map.getBounds()
    setBBoxFromMapbox(bounds)
  }

  const polygonsFillOpacity = useFillOpacity()

  return (
    <Mapbox
      id={MAP_ID}
      fitBounds={bbox}
      fitBoundsOptions={{
        duration: 50,
        linear: true,
        padding: ALERT_MAP_PADDING,
      }}
      style={{
        height: "100%",
        width: "100%",
      }}
      onMoveEnd={handleMapMove}
    >
      {/* CONTROLS */}
      <GeolocateControl />
      <NavigationControl />

      {/* ---------- ZONES ----------- */}
      {!!showServiceZones && !!serviceZones && (
        <GeoJSONLayer
          id="alerts_map_service"
          data={getZonesGeojson(serviceZones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.purple["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.purple["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      {!!showDropzones && !!dropzones && (
        <GeoJSONLayer
          id="alerts_map_dropzones"
          data={getZonesGeojson(dropzones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.blue["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.blue["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      {!!showMaintenanceZones && !!maintenanceZones && (
        <GeoJSONLayer
          id="alerts_map_maintenance"
          data={getZonesGeojson(maintenanceZones) as GeoJSON.FeatureCollection}
          linePaint={{
            lineColor: theme.colors.gray["800"],
          }}
          fillPaint={{
            fillColor: theme.colors.gray["500"],
            fillOpacity: polygonsFillOpacity,
          }}
        />
      )}

      {isLoading && (
        <Center pos="absolute" w="100%" bottom={8}>
          <Center p={2} bg="white" borderRadius="md" h={50} w={50}>
            <Spinner size="sm" />
          </Center>
        </Center>
      )}

      {/* ---------- ALERTS ----------- */}
      <ErrorBoundary>
        <AlertsMapMapboxSupercluster
          zoomOnClick
          groupedAlertsEntries={groupedAlertsEntries}
          refetchAlerts={refetchAlerts}
          selectedBikeUuid={selectedBikeUuid}
          setSelectedBikeUuid={setSelectedBikeUuid}
          onDeselectBike={onDeselectBike}
          selectedAlertUuid={selectedAlertUuid}
          onDeselectAlert={onDeselectAlert}
        />
      </ErrorBoundary>
    </Mapbox>
  )
}

export default AlertsMapMapbox
